import React from 'react';
import {
  Box,
  Flex,
  Text,
  Grid,
  Icon,
  HStack,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  HiOutlineTrendingUp,
} from 'react-icons/hi';
import {
  FaHashtag,
  FaGlobe,
  FaSmile,
  FaSearch,
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useUser } from '../contexts/UserContext';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

/**
 * Calculate Localytics Score from 5 category ranks (0-100 scale)
 * Each category contributes up to 20 points based on rank position
 * Formula per category: max(0, 21 - rank)
 * 1st = 20 points, 2nd = 19, ..., 20th = 1 point, 21st+ = 0 points
 * Perfect score is 100 (if #1 in all 5 categories: 20+20+20+20+20 = 100)
 */
const calculateLocalyticsScore = (growthRank, totalReviewsRank, trafficRank, sentimentRank, seoRank, totalBusinesses) => {
  const categories = [
    { rank: growthRank },
    { rank: totalReviewsRank },
    { rank: trafficRank },
    { rank: sentimentRank },
    { rank: seoRank }
  ];
  
  let totalScore = 0;
  let validCategories = 0;
  
  categories.forEach(({ rank }) => {
    if (rank && rank !== 'N/A' && !isNaN(rank) && rank > 0) {
      // 1st = 20 points, 2nd = 19, ..., 20th = 1 point, 21st+ = 0 points
      const categoryScore = Math.max(0, 21 - rank);
      totalScore += categoryScore;
      validCategories++;
    }
  });
  
  if (validCategories === 0) return 0;
  
  return Math.round(totalScore);
};

/**
 * Get the tier label based on score rank position (groups of 3).
 * Falls back to score-based tiers if rank is unavailable.
 */
const getTierLabel = (score, scoreRank, totalBusinesses) => {
  if (!score && score !== 0) return { label: 'N/A', emoji: '📊', color: 'gray.500' };

  if (scoreRank && scoreRank > 0) {
    if (scoreRank <= 3) return { label: 'Excellent', emoji: '⚡', color: 'blue.500' };
    if (scoreRank <= 6) return { label: 'Good', emoji: '✅', color: 'green.500' };
    if (scoreRank <= 9) return { label: 'Fair', emoji: '📊', color: 'teal.500' };
    if (scoreRank <= 12) return { label: 'Below Average', emoji: '📉', color: 'yellow.600' };
    if (scoreRank <= 15) return { label: 'Poor', emoji: '⚠️', color: 'orange.500' };
    return { label: 'Needs Improvement', emoji: '🔧', color: 'red.500' };
  }

  if (score >= 85) return { label: 'Excellent', emoji: '⚡', color: 'blue.500' };
  if (score >= 70) return { label: 'Good', emoji: '✅', color: 'green.500' };
  if (score >= 55) return { label: 'Fair', emoji: '📊', color: 'teal.500' };
  if (score >= 40) return { label: 'Below Average', emoji: '📉', color: 'yellow.600' };
  if (score >= 25) return { label: 'Poor', emoji: '⚠️', color: 'orange.500' };
  return { label: 'Needs Improvement', emoji: '🔧', color: 'red.500' };
};

/**
 * Get color for category chip based on score (points out of 20)
 * Matches the leaderboard ranking card color scheme:
 * >= 15 pts = green, >= 10 pts = blue, >= 5 pts = orange, < 5 pts = gray
 */
const getScoreColor = (score) => {
  if (score === null || score === undefined) return '#A0AEC0'; // gray.500
  if (score >= 15) return '#38A169'; // green.500
  if (score >= 10) return '#3182CE'; // blue.500
  if (score >= 5) return '#DD6B20'; // orange.500
  return '#A0AEC0'; // gray.500
};

/**
 * Main Score Circle Component - Compact version
 */
const LocalyticsScoreCircle = ({ score, tierInfo }) => {
  const displayScore = (score || score === 0) ? Math.round(score) : 'N/A';
  const { brandName } = useUser();
  
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const scoreColor = useColorModeValue('gray.900', 'white');

  const pillBgNA = useColorModeValue('gray.400', 'gray.500');
  const pillBgExcellent = useColorModeValue('blue.500', 'blue.400');
  const pillBgGood = useColorModeValue('green.500', 'green.400');
  const pillBgFair = useColorModeValue('teal.500', 'teal.400');
  const pillBgBelowAverage = useColorModeValue('yellow.500', 'yellow.400');
  const pillBgPoor = useColorModeValue('orange.500', 'orange.400');
  const pillBgNeedsImprovement = useColorModeValue('red.500', 'red.400');

  const getTierPillBg = () => {
    if (displayScore === 'N/A') return pillBgNA;
    const label = tierInfo.label;
    if (label === 'Excellent') return pillBgExcellent;
    if (label === 'Good') return pillBgGood;
    if (label === 'Fair') return pillBgFair;
    if (label === 'Below Average') return pillBgBelowAverage;
    if (label === 'Poor') return pillBgPoor;
    if (label === 'Needs Improvement') return pillBgNeedsImprovement;
    return pillBgNA;
  };

  return (
    <MotionBox
      initial={{ scale: 0.98, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.4, type: "spring" }}
      w="100%"
      h="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box
        bg="white"
        borderRadius="2xl"
        px={4}
        py={3}
        textAlign="center"
        w="100%"
      >
        <Text fontSize="14px" lineHeight="1" mb={1}>
          {tierInfo.emoji}
        </Text>

        <Text
          fontSize="42px"
          fontWeight="700"
          color={scoreColor}
          lineHeight="0.95"
          letterSpacing="-0.03em"
          mt={1}
          mb={1.5}
        >
          {displayScore}
        </Text>

        <Text
          fontSize="9px"
          fontWeight="600"
          color={labelColor}
          textTransform="uppercase"
          letterSpacing="0.18em"
          mb={2.5}
        >
          {brandName} Score
        </Text>

        <Flex justify="center">
          <Box
            bg={getTierPillBg()}
            color="white"
            px={4}
            py={1}
            borderRadius="full"
            fontSize="11px"
            fontWeight="600"
            textTransform="uppercase"
            letterSpacing="0.08em"
            minW="120px"
          >
            {tierInfo.label}
          </Box>
        </Flex>
      </Box>
    </MotionBox>
  );
};

/**
 * Formula Score Chip - Shows individual category score (out of 20) with icon
 */
const FormulaRankChip = ({ rank, score, label, icon, total, delay = 0 }) => {
  const isValid = rank !== 'N/A' && rank !== null && rank !== undefined;
  const displayScore = score || 0;
  
  const color = getScoreColor(displayScore);
  const chipBg = useColorModeValue('white', 'gray.700');
  
  return (
    <MotionFlex
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay }}
      direction="column"
      align="center"
      gap={1.5}
    >
      <Flex
        bg={chipBg}
        borderRadius="xl"
        px={3.5}
        py={2}
        align="center"
        justify="center"
        border="1px solid"
        borderColor="gray.100"
        transition="all 0.2s ease"
        _hover={{ transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}
      >
        <HStack spacing={1.5} align="center" h="17px">
          <Flex
            w="14px"
            h="17px"
            justify="center"
            align="center"
            flexShrink={0}
          >
            <Icon as={icon} boxSize="12px" color={color} opacity={0.7} />
          </Flex>
          <Flex h="17px" align="center" justify="center" transform="translateY(2px)">
            <Text
              fontSize="17px"
              fontWeight="700"
              color={color}
              lineHeight="17px"
            >
              {isValid ? displayScore : '—'}
            </Text>
          </Flex>
        </HStack>
      </Flex>
      <Text
        fontSize="9.5px"
        fontWeight="700"
        color="gray.400"
        textTransform="uppercase"
        letterSpacing="0.5px"
      >
        {label}
      </Text>
    </MotionFlex>
  );
};

/**
 * Formula Operator - Plus sign or equals
 */
const FormulaOperator = ({ children, delay = 0 }) => (
  <MotionBox
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.25, delay }}
    alignSelf="flex-start"
    mt="9px"
  >
    <Text
      fontSize="16px"
      fontWeight="400"
      color="gray.300"
      mx={0.5}
      userSelect="none"
      lineHeight="17px"
    >
      {children}
    </Text>
  </MotionBox>
);

/**
 * LocalyticsRankingCard - Simple score card with formula visualization
 * 
 * Shows: Growth Points + Reviews Points + Traffic Points + Sentiment Points + SEO Points = Score (out of 100)
 * Each category contributes up to 20 points: 1st = 20 pts, 2nd = 19 pts, ..., 20th = 1 pt, 21st+ = 0 pts
 */
const LocalyticsRankingCard = ({
  growthRank,
  totalGrowthBusinesses,
  totalReviewsRank,
  totalReviewsBusinesses,
  trafficRank,
  totalTrafficBusinesses,
  sentimentRank,
  totalSentimentBusinesses,
  seoRank,
  totalSeoBusinesses,
  scoreRank,
  totalScoreBusinesses,
  rightSlot,
}) => {
  const totalBusinesses = {
    growth: totalGrowthBusinesses,
    reviews: totalReviewsBusinesses,
    traffic: totalTrafficBusinesses,
    sentiment: totalSentimentBusinesses,
    seo: totalSeoBusinesses
  };
  
  const localyticsScore = calculateLocalyticsScore(
    growthRank,
    totalReviewsRank,
    trafficRank,
    sentimentRank,
    seoRank,
    totalBusinesses
  );
  
  const tierInfo = getTierLabel(localyticsScore, scoreRank, totalScoreBusinesses);
  
  const hasValidData = [growthRank, totalReviewsRank, trafficRank, sentimentRank, seoRank].some(
    r => r !== 'N/A' && r !== null && r !== undefined
  );

  // Calculate individual category scores for display
  // 1st = 20 points, 2nd = 19, ..., 20th = 1 point, 21st+ = 0 points
  const calculateCategoryScore = (rank, total) => {
    if (!rank || rank === 'N/A') return 0;
    return Math.max(0, 21 - rank);
  };
  
  const growthScore = calculateCategoryScore(growthRank, totalGrowthBusinesses);
  const reviewsScore = calculateCategoryScore(totalReviewsRank, totalReviewsBusinesses);
  const trafficScore = calculateCategoryScore(trafficRank, totalTrafficBusinesses);
  const sentimentScore = calculateCategoryScore(sentimentRank, totalSentimentBusinesses);
  const seoScore = calculateCategoryScore(seoRank, totalSeoBusinesses);

  if (!hasValidData) {
    return (
      <Box
        bg="white"
        borderRadius="2xl"
        p={6}
        border="1px solid"
        borderColor="gray.100"
        boxShadow="0 1px 3px rgba(0,0,0,0.04)"
      >
        <VStack spacing={3} py={6}>
          <Text fontSize="13px" color="gray.400" fontWeight="500">
            Ranking data unavailable
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <Box
        bg="white"
        borderRadius="2xl"
        p={4}
        border="1px solid"
        borderColor="gray.100"
        boxShadow="0 1px 3px rgba(0,0,0,0.04)"
      >
        <Grid
          templateColumns={
            rightSlot
              ? "180px minmax(0, 1fr) minmax(0, 1fr)"
              : "220px 1fr"
          }
          gap={4}
          alignItems="stretch"
        >
          {/* Left: Localytics Score Circle */}
          <LocalyticsScoreCircle 
            score={localyticsScore}
            tierInfo={tierInfo}
          />

          {/* Middle (or right when no rightSlot): Formula Explanation */}
          <Box display="flex" flexDirection="column" justifyContent="center">
            {/* Section Label */}
            <Text 
              fontSize="10px" 
              fontWeight="600" 
              color="gray.400" 
              textTransform="uppercase" 
              letterSpacing="0.8px"
              mb={2}
            >
              How it's calculated
            </Text>
            
            {/* Formula Row - Shows score contribution from each category */}
            <Flex 
              align="flex-start" 
              justify="center"
              wrap="wrap"
              gap={rightSlot ? 1.5 : 2.5}
              bg="gray.50"
              borderRadius="xl"
              px={rightSlot ? 2.5 : 4}
              py={rightSlot ? 3 : 4}
              border="1px solid"
              borderColor="gray.100"
            >
              <FormulaRankChip 
                rank={growthRank}
                score={growthScore}
                label="Growth" 
                icon={HiOutlineTrendingUp}
                total={totalGrowthBusinesses}
                delay={0.1}
              />
              <FormulaOperator delay={0.15}>+</FormulaOperator>
              
              <FormulaRankChip 
                rank={totalReviewsRank}
                score={reviewsScore}
                label="Reviews" 
                icon={FaHashtag}
                total={totalReviewsBusinesses}
                delay={0.2}
              />
              <FormulaOperator delay={0.25}>+</FormulaOperator>
              
              <FormulaRankChip 
                rank={trafficRank}
                score={trafficScore}
                label="Traffic" 
                icon={FaGlobe}
                total={totalTrafficBusinesses}
                delay={0.3}
              />
              <FormulaOperator delay={0.35}>+</FormulaOperator>
              
              <FormulaRankChip 
                rank={sentimentRank}
                score={sentimentScore}
                label="Sentiment" 
                icon={FaSmile}
                total={totalSentimentBusinesses}
                delay={0.4}
              />
              <FormulaOperator delay={0.45}>+</FormulaOperator>
              
              <FormulaRankChip 
                rank={seoRank}
                score={seoScore}
                label="SEO" 
                icon={FaSearch}
                total={totalSeoBusinesses}
                delay={0.5}
              />
              
              <FormulaOperator delay={0.55}>=</FormulaOperator>

              <MotionFlex
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.6 }}
                direction="column"
                align="center"
                gap={0.5}
                alignSelf="flex-start"
                mt="9px"
              >
                <Text
                  fontSize={rightSlot ? "22px" : "26px"}
                  fontWeight="900"
                  lineHeight="1"
                  color={localyticsScore >= 75 ? 'green.500' : localyticsScore >= 50 ? 'blue.500' : localyticsScore >= 25 ? 'orange.500' : 'gray.500'}
                >
                  {localyticsScore}
                </Text>
                <Text fontSize="8.5px" fontWeight="600" color="gray.350" textTransform="uppercase" letterSpacing="0.4px">
                  / 100
                </Text>
              </MotionFlex>
            </Flex>
            
            {/* Result explanation */}
            <Flex justify="center" mt={2.5} gap={4} wrap="wrap">
              <HStack spacing={1.5}>
                <Box w="6px" h="6px" borderRadius="full" bg="gray.300" />
                <Text mb={0} fontSize="11px" color="gray.400" fontWeight="500">Each category: <Text as="span" fontWeight="700" color="gray.600">up to 20 pts</Text></Text>
              </HStack>
              <HStack spacing={1.5}>
                <Box w="6px" h="6px" borderRadius="full" bg="green.300" />
                <Text mb={0} fontSize="11px" color="gray.400" fontWeight="500">Perfect score: <Text as="span" fontWeight="700" color="green.500">100</Text></Text>
              </HStack>
            </Flex>
          </Box>

          {/* Right: optional slot for AI Immediate Action */}
          {rightSlot && (
            <Box
              borderLeft='1px solid'
              borderTop='none'
              borderColor='gray.100'
              pl={4}
              pt={0}
              minW={0}
            >
              {rightSlot}
            </Box>
          )}
        </Grid>
      </Box>
    </MotionBox>
  );
};

export default LocalyticsRankingCard;
